import { makeStyles, tokens } from '@fluentui/react-components';
import { useT, useViewDataSource } from '@powerportalspro/react';
import {
  AggregateDataverseChartDataSource,
  QueryAggregate,
  DateGrouping,
  ChartType,
} from '@powerportalspro/react-charts';
import {
  DataverseChart,
  GridToolbarItems,
  MainGrid,
  NavigateOpenRecordGridButton,
  Section,
  SectionColumn,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// An opportunity dashboard: three aggregate charts, then a pie chart and a grid that share one
// data source. One request returns both the grid's rows and the pie's aggregation, so the two
// never disagree; clicking a slice highlights the matching rows without fetching again.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.Dashboard.DashboardMainDemo';

const OPEN_OPPORTUNITIES_VIEW_ID = 'a1b2c3d4-e5f6-4789-abcd-112233445566';
const RECENTLY_WON_VIEW_ID = 'b2c3d4e5-f6a7-4890-bcde-223344556677';

// Shared by the "Open Opportunities" view and the data source, so the rows the pie aggregates are
// the rows the grid shows.
const OPEN_OPPORTUNITIES_FETCH_XML = `<fetch>
  <entity name='opportunity'>
    <attribute name='name' />
    <attribute name='customerid' />
    <attribute name='estimatedvalue' />
    <attribute name='estimatedclosedate' />
    <attribute name='opportunityratingcode' />
    <attribute name='closeprobability' />
    <filter>
      <condition attribute='statecode' operator='eq' value='0' />
    </filter>
    <order attribute='estimatedclosedate' />
  </entity>
</fetch>`;

const RECENTLY_WON_FETCH_XML = `<fetch>
  <entity name='opportunity'>
    <attribute name='name' />
    <attribute name='customerid' />
    <attribute name='actualvalue' />
    <attribute name='actualclosedate' />
    <attribute name='opportunityratingcode' />
    <filter type='and'>
      <condition attribute='statecode' operator='eq' value='1' />
      <condition attribute='actualclosedate' operator='last-x-months' value='3' />
    </filter>
    <order attribute='actualclosedate' descending='true' />
  </entity>
</fetch>`;

const GRID_VIEW_IDS = [OPEN_OPPORTUNITIES_VIEW_ID, RECENTLY_WON_VIEW_ID];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
});

export function DashboardMainSample() {
  const styles = useStyles();
  const t = useT();

  // Kept in useMemo so each chart keeps one source object, and so loads once.
  const pipelineSource = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'purchasetimeframe',
        aggregateColumn: 'estimatedvalue',
        aggregate: QueryAggregate.Sum,
        filterXml: "<filter><condition attribute='statecode' operator='eq' value='0' /></filter>",
        singleSeriesLabel: t(`${STRINGS_BASE}.pipeline-series-label`),
      }),
    [t],
  );

  const revenueByYearSource = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'actualclosedate',
        groupByDateGrouping: DateGrouping.Year,
        aggregateColumn: 'estimatedvalue',
        aggregate: QueryAggregate.Sum,
        seriesColumn: 'statecode',
        filterXml: "<filter><condition attribute='statecode' operator='ne' value='0' /></filter>",
      }),
    [],
  );

  const yoySource = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'actualclosedate',
        groupByDateGrouping: DateGrouping.MonthOfYear,
        seriesDateGrouping: DateGrouping.Year,
        aggregateColumn: 'estimatedvalue',
        aggregate: QueryAggregate.Sum,
        filterXml: "<filter><condition attribute='statecode' operator='eq' value='1' /></filter>",
      }),
    [],
  );

  // The open-opportunities view exists only here, not as a saved view in Dataverse, so the data
  // source gets its FetchXML to run and its id alongside, which the server uses to find the view's
  // localized column titles.
  const sharedDataSource = useViewDataSource({
    viewId: OPEN_OPPORTUNITIES_VIEW_ID,
    fetchXml: OPEN_OPPORTUNITIES_FETCH_XML,
    aggregations: [
      {
        groupBy: { column: 'opportunityratingcode' },
        aggregateColumn: 'opportunityid',
        aggregate: QueryAggregate.Count,
      },
    ],
  });

  const gridViews = useMemo<ViewDefinition[]>(
    () => [
      {
        id: OPEN_OPPORTUNITIES_VIEW_ID,
        tableName: 'opportunity',
        displayName: t(`${STRINGS_BASE}.view-open-opportunities`),
        fetchXml: OPEN_OPPORTUNITIES_FETCH_XML,
        columns: [
          { columnName: 'name', width: 250 },
          { columnName: 'customerid', width: 200 },
          { columnName: 'estimatedvalue', width: 130 },
          { columnName: 'estimatedclosedate', width: 140 },
          { columnName: 'opportunityratingcode', width: 100 },
          { columnName: 'closeprobability', width: 100 },
        ],
      },
      {
        id: RECENTLY_WON_VIEW_ID,
        tableName: 'opportunity',
        displayName: t(`${STRINGS_BASE}.view-recently-won`),
        fetchXml: RECENTLY_WON_FETCH_XML,
        columns: [
          { columnName: 'name', width: 250 },
          { columnName: 'customerid', width: 200 },
          { columnName: 'actualvalue', width: 130 },
          { columnName: 'actualclosedate', width: 140 },
          { columnName: 'opportunityratingcode', width: 100 },
        ],
      },
    ],
    [t],
  );

  return (
    <div className={styles.root}>
      <Section borderVisible={false}>
        <SectionColumn width={2}>
          <DataverseChart
            dataSource={pipelineSource}
            type={ChartType.Doughnut}
            title={t(`${STRINGS_BASE}.pipeline-chart-title`)}
            height="350px"
            showExportButton={false}
          />
        </SectionColumn>
        <SectionColumn width={3}>
          <DataverseChart
            dataSource={revenueByYearSource}
            type={ChartType.Bar}
            title={t(`${STRINGS_BASE}.revenue-chart-title`)}
            stacked
            yAxisPrefix="$"
            height="350px"
            showExportButton={false}
          />
        </SectionColumn>
      </Section>

      <Section>
        <SectionColumn>
          <DataverseChart
            dataSource={yoySource}
            type={ChartType.Line}
            title={t(`${STRINGS_BASE}.yoy-chart-title`)}
            yAxisPrefix="$"
            height="350px"
            showExportButton={false}
          />
        </SectionColumn>
      </Section>

      <Section headerText={t(`${STRINGS_BASE}.linked-grid-section-header`)} borderVisible>
        <SectionColumn width={1}>
          <DataverseChart
            dataSource={sharedDataSource}
            type={ChartType.Pie}
            title={t(`${STRINGS_BASE}.rating-chart-title`)}
            height="350px"
            showExportButton={false}
          />
        </SectionColumn>
        <SectionColumn width={3}>
          <MainGrid
            dataSource={sharedDataSource}
            title={t(`${STRINGS_BASE}.grid-title`)}
            tableName="opportunity"
            viewIds={GRID_VIEW_IDS}
            customViewDefinitions={gridViews}
            defaultViewId={OPEN_OPPORTUNITIES_VIEW_ID}
            minHeight="350px"
            maxHeight="350px"
          >
            <GridToolbarItems>
              <NavigateOpenRecordGridButton
                urlFor={(record) => `demos/opportunity?opportunityId=${record.id}`}
              />
            </GridToolbarItems>
          </MainGrid>
        </SectionColumn>
      </Section>
    </div>
  );
}
